/**
 * TypeScript mirror of the `/bo-api` contract described in docs/bo-api.md.
 * All dates are plain ISO 8601 UTC strings on this API (the Trium wire format
 * is only used on `/api/WalletTm/*`).
 */

export type StatutCommande = 0 | 1 | 2 | 3;
export type PolitiqueTransfert = 0 | 1 | 2 | 3 | 4;
export type StatutBillet = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;
/** 0 = Code128, anything else = QR code (guide §5.2). */
export type TypeCodeBarre = 0 | 1;

export interface ForcedError {
  code: number;
  description: string;
  httpStatus: number;
  once: boolean;
}

/** Endpoint names usable as `forcedErrors` keys — the 15 v1 Trium routes (guide §1.3). */
export const ENDPOINT_NAMES = [
  'WalletConnexion',
  'WalletSecuriteCommande',
  'WalletDetailCommande',
  'WalletStatutBillet',
  'WalletAccepterTransfert',
  'WalletDemandeTransfertBillet',
  'WalletAnnuleTransfert',
  'WalletGetHeure',
  'WalletGenerationMticket',
  'WalletBilletConfirmation',
  'WalletConfirmationReceptionCodeBarre',
  'WalletConfirmationAffichageCodeBarre',
  'WalletConfirmationReceptionDateAffichage',
  'Installations',
  'WalletMiseAjour',
] as const;

export type EndpointName = (typeof ENDPOINT_NAMES)[number];

export interface Settings {
  clockOffsetSeconds: number;
  deeplinkDomain: string | null;
  forcedErrors: Record<string, ForcedError>;
  jwtTtlSeconds: number;
  /** Simulated clock = real now + clockOffsetSeconds. */
  serverNow: string;
}

export type SettingsUpdate = Partial<Pick<Settings, 'clockOffsetSeconds' | 'deeplinkDomain' | 'forcedErrors'>>;

export interface Lieu {
  idLieu: number;
  libelle: string;
  ville: string;
  adresse: string;
  latitude: number;
  longitude: number;
  /** IANA timezone, e.g. "Europe/Paris". */
  timezone: string;
}

export interface Manifestation {
  idManif: number;
  libelle: string;
  genre: string;
  sousGenre: string;
  urlVisuel: string;
  infosProducteur: string | null;
  numeroLicence: string | null;
  infoSpec: string | null;
}

export interface Seance {
  idSeance: number;
  idManif: number;
  idLieu: number;
  dateSeance: string;
  dateFinSeance: string;
  dateInitialeSeance: string | null;
}

export interface InfoPlacement {
  /** PascalCase on purpose: Trium's casing quirk (guide §5.3). */
  LibellePlace: string;
  ValeurPlace: string;
}

export interface CommandeSummary {
  guid: string;
  idCommande: number;
  nom: string;
  prenom: string;
  libelleManif: string;
  libelleLieu: string;
  ville: string;
  dateSeance: string;
  statut: StatutCommande;
  transfert: PolitiqueTransfert;
  eticket: boolean;
  envoiCodeBarreDiffere: boolean;
  dateAffichageCodeBarre: string | null;
  flagAction: boolean;
  flagActionCodeBarre: boolean;
  nbBillets: number;
  /** Billets dont `numeroCodeBarre` est non vide. */
  nbBilletsAvecQr: number;
  parentGuid: string | null;
  createdAt: string;
}

export interface Commande {
  guid: string;
  idCommande: number;
  nom: string;
  prenom: string;
  statut: StatutCommande;
  eticket: boolean;
  transfert: PolitiqueTransfert;
  envoiCodeBarreDiffere: boolean;
  dateAffichageCodeBarre: string | null;
  texteComplement: string | null;
  typeCodeBarre: TypeCodeBarre;
  idTiersDistributeur: number;
  flagAction: boolean;
  flagActionCodeBarre: boolean;
  parentGuid: string | null;
  createdAt: string;

  idSeance: number;
  dateSeance: string;
  dateFinSeance: string;
  dateInitialeSeance: string | null;
  idManif: number;
  libelleManif: string;
  genreManif: string;
  sousGenreManif: string;
  urlVisuel: string;
  infosProducteur: string | null;
  numeroLicence: string | null;
  infoSpecManifestation: string | null;
  idLieu: number;
  libelleLieu: string;
  ville: string;
  adresseLieu: string;
  longitudeLieu: number;
  latitudeLieu: number;
  timezone: string;
}

export interface Billet {
  idBillet: number;
  guidbil: string;
  /** null = not delivered yet; "" = withdrawn after a cancelled transfer (guide §4.3). */
  numeroCodeBarre: string | null;
  statutBillet: StatutBillet;
  prixBillet: number;
  /** Base64 PNG. */
  maquetteBillet: string;
  infoPlacement: InfoPlacement[];
  nomBeneficiaire: string | null;
  prenomBeneficiaire: string | null;
  dateImport: string | null;
  dateTransfert: string | null;
  dateAnnulation: string | null;
  dateMiseRevente: string | null;
  dateRevente: string | null;
}

export type TokenType = 'i' | 't';

export interface Token {
  x: string;
  guidCommande: string;
  type: TokenType;
  idBillet: number | null;
  createdAt: string;
  consumedAt: string | null;
  revokedAt: string | null;
  /** Deeplink built from settings.deeplinkDomain. */
  url: string;
}

export type EtatTransfert = 'pending' | 'accepted' | 'cancelled' | 'expired';

export interface Transfert {
  id: string;
  guidEmetteur: string;
  idBillet: number;
  guidbil: string;
  tokenX: string;
  guidRecepteur: string | null;
  etat: EtatTransfert;
  nom: string;
  prenom: string;
  createdAt: string;
  acceptedAt: string | null;
  cancelledAt: string | null;
}

export interface JournalEntry {
  id: string;
  ts: string;
  method: string;
  path: string;
  endpoint: string | null;
  query: Record<string, string>;
  guid: string | null;
  headers: Record<string, string>;
  body: string | null;
  status: number;
  note: string | null;
}

export interface JournalFilters {
  guid?: string;
  endpoint?: string;
  /** Client-side filter, applied after fetch. */
  status?: string;
  limit?: number;
  before?: string;
}

export interface Scenario {
  name: string;
  title: string;
  description: string;
}

export interface Link {
  x: string;
  url: string;
  type?: TokenType;
}

export interface CommandeDetail {
  commande: Commande;
  billets: Billet[];
  tokens: Token[];
  transferts: Transfert[];
  preview: {
    /** Exactly what WalletDetailCommande would return now. */
    detail: Record<string, unknown>;
    /** What WalletStatutBillet would return now. */
    statut: Record<string, unknown>;
  };
}

export interface CreateCommandeResult {
  commande: Commande;
  billets: Billet[];
  link: Link;
}

export interface ActionResult {
  commande: Commande;
  billets: Billet[];
}

export interface NewBilletInput {
  prixBillet: number;
  infoPlacement: InfoPlacement[];
  withBarcode: boolean;
}

export interface CreateCommandeInput {
  idSeance: number;
  nom: string;
  prenom: string;
  eticket: boolean;
  transfert: PolitiqueTransfert;
  envoiCodeBarreDiffere: boolean;
  dateAffichageCodeBarre: string | null;
  texteComplement: string | null;
  typeCodeBarre: TypeCodeBarre;
  billets: NewBilletInput[];
}

/** Flag raised by an action — used for the UI hints (docs/bo-api.md, guide §6.2). */
export type FlagName = 'action' | 'actionCodeBarre' | 'none';

/** Discriminated union of every `POST /bo-api/commandes/:guid/actions/:action` body. */
export type Action =
  | { action: 'deliverBarcodes'; body: Record<string, never> }
  | { action: 'withdrawBarcodes'; body: Record<string, never> }
  | { action: 'setDisplayDate'; body: { date: string | null } }
  | { action: 'setDeferred'; body: { envoiCodeBarreDiffere: boolean } }
  | { action: 'setTransferPolicy'; body: { transfert: PolitiqueTransfert } }
  | {
      action: 'setOrderStatus';
      body: { statut: StatutCommande; dateSeance?: string; dateInitialeSeance?: string };
    }
  | { action: 'setPlacement'; body: { idBillet: number; infoPlacement: InfoPlacement[] } }
  | { action: 'setResale'; body: { idBillet: number } }
  | { action: 'setSold'; body: { idBillet: number } }
  | { action: 'cancelTicket'; body: { idBillet: number } }
  | { action: 'resetTicket'; body: { idBillet: number } }
  | { action: 'simulateTransferCancelled'; body: { idBillet: number } }
  | { action: 'simulateTransferExpired'; body: { idBillet: number } }
  | {
      action: 'setMaquette';
      body: { idBillet: number; maquetteBillet: string; flag: FlagName };
    }
  | { action: 'setFlags'; body: { action?: boolean; actionCodeBarre?: boolean } };

export type ActionName = Action['action'];
